// ClientRefundsTab.jsx
// The client's Refund tab — now CONNECTED to the main Refunds section instead of
// the old per-trip refund subdocument. Refunds here are real CrmRefund records:
//   • list   → GET    /crm/refunds?client=<id>
//   • create → POST   /crm/refunds         (client pre-linked → shows in both places)
//   • edit   → PATCH  /crm/refunds/:id
//   • status → PATCH  /crm/refunds/:id/status
//   • delete → DELETE /crm/refunds/:id
// Display + form reuse the exact same components as the standalone Refunds section
// (../refunds/RefundDetailPanel, ../refunds/RefundForm) so both sides match.
// A refund can also have CRM tasks created against it (linked via refundId).
import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { useToast, useConfirm, Btn, Spinner, ApiError } from "../ui";
import { Ico } from "./primitives";
import RefundDetailPanel from "../refund/RefundDetailPanel";
import RefundForm from "../refund/RefundForm";
import QuickTaskModal from "./QuickTaskModal";

import { BRAND } from "../brand";

const fmtMoney = (n, cur = "USD") =>
  n != null
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: cur, minimumFractionDigits: 2 }).format(n)
    : "–";

const STATUS_COLORS = {
  not_started: "bg-slate-100 text-slate-500",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-emerald-100 text-emerald-700",
};
const STATUS_LABELS = {
  not_started: "Not Started",
  in_progress: "In Progress",
  completed: "Completed",
};

export default function ClientRefundsTab({ client, onAddTask, onChanged }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(null);   // refund being viewed
  const [refundTasks, setRefundTasks] = useState([]);
  const [modal, setModal] = useState(null);          // null | "create" | "edit" | "task"
  const [saving, setSaving] = useState(false);
  const [actionSaving, setActionSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/crm/refunds?client=${client._id}&limit=100`);
      setList(res.data || res || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [client._id]);

  useEffect(() => { load(); }, [load]);

  // Pre-fill a new refund from the client + their latest trip.
  const latestTrip = client.trips?.[client.trips.length - 1];
  const refundInitial = {
    client: client._id,
    clientName: client.fullName,
    email: client.email,
    phone: client.phone,
    country: client.countryOfResidence,
    destination: latestTrip?.destinations?.join(", ") || "",
    currency: latestTrip?.payment?.currency || BRAND.currency,
  };

  const doCreate = async (form) => {
    setSaving(true);
    try {
      await api.post("/crm/refunds", { ...form, client: client._id });
      toast("Refund created ✓");
      setModal(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doEdit = async (form) => {
    setSaving(true);
    try {
      const updated = await api.patch(`/crm/refunds/${selected._id}`, { ...form, client: client._id });
      toast("Refund updated ✓");
      setModal(null);
      setSelected(updated || selected);
      await load();
      onChanged?.();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doStatus = async (status) => {
    if (!selected) return;
    setActionSaving(true);
    try {
      await api.patch(`/crm/refunds/${selected._id}/status`, { refundStatus: status });
      const fresh = await api.get(`/crm/refunds/${selected._id}`);
      setSelected(fresh);
      await load();
      onChanged?.();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setActionSaving(false);
    }
  };

  const doDelete = async (id) => {
    if (!(await confirm({ title: "Delete refund?", message: "This refund will be permanently removed. This cannot be undone.", confirmText: "Delete refund" }))) return;
    try {
      await api.delete(`/crm/refunds/${id}`);
      toast("Refund deleted");
      setSelected(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const openDetail = async (r) => {
    try {
      const full = await api.get(`/crm/refunds/${r._id}`);
      setSelected(full);
      api.get(`/crm/tasks/by-refund/${r._id}`).then(setRefundTasks).catch(() => setRefundTasks([]));
    } catch (e) {
      toast(e.message, "error");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">
          {list.length} refund{list.length !== 1 ? "s" : ""} · synced with the Refunds section
        </span>
        <Btn variant="primary" size="sm" onClick={() => setModal("create")}>
          <Ico d="M12 4v16m8-8H4" sz={11} col="white" /> New Refund
        </Btn>
      </div>

      {error && <ApiError msg={error} onRetry={load} />}
      {loading && <Spinner />}

      {!loading && !error && (
        !list.length ? (
          <div className="flex flex-col items-center justify-center py-14 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
            <span className="text-3xl mb-2">💸</span>
            <p className="text-sm font-bold text-slate-500 mb-1">No refunds for this client</p>
            <p className="text-xs text-slate-400 mb-3">Create one — it’ll appear in the Refunds section too.</p>
            <Btn variant="primary" size="sm" onClick={() => setModal("create")}>
              <Ico d="M12 4v16m8-8H4" sz={12} col="white" /> New Refund
            </Btn>
          </div>
        ) : (
          <div className="space-y-2">
            {list.map((r) => {
              const isKai = r.refundType === "kai_credit";
              return (
                <button
                  key={r._id}
                  onClick={() => openDetail(r)}
                  className="w-full text-left rounded-xl border border-slate-200 hover:border-[rgb(var(--brand-rgb))] hover:shadow-sm transition-all p-4 flex items-center gap-4"
                >
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center text-white text-[10px] font-black flex-shrink-0 ${isKai ? "bg-[rgb(var(--brand-rgb))]" : "bg-amber-500"}`}
                  >
                    {isKai ? BRAND.shortUpper : "REF"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-slate-800 text-sm truncate">{r.refundReference}</p>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[r.refundStatus] || "bg-slate-100 text-slate-500"}`}>
                        {STATUS_LABELS[r.refundStatus] || r.refundStatus}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 truncate">
                      {r.destination || "—"}
                      {r.refundMethod && ` · ${r.refundMethod.replace(/_/g, " ")}`}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-black ${isKai ? "text-[rgb(var(--brand-rgb))]" : "text-amber-600"}`}>
                      {fmtMoney(r.refundAmount, r.currency)}
                    </p>
                    <p className="text-[10px] text-slate-400">{r.currency || "USD"}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}

      {/* ── Detail overlay (reuses the shared RefundDetailPanel) ── */}
      {selected && (
        <div
          className="fixed inset-0 z-[65] flex items-center justify-center p-4"
          style={{ background: "rgba(10,15,30,0.6)", backdropFilter: "blur(8px)" }}
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden"
            style={{ animation: "modalIn .2s ease" }}
            onClick={(e) => e.stopPropagation()}
          >
            <RefundDetailPanel
              refund={selected}
              tasks={refundTasks}
              onClose={() => setSelected(null)}
              onEdit={() => setModal("edit")}
              onStatusChange={doStatus}
              onAddTask={() => setModal("task")}
              onDelete={() => doDelete(selected._id)}
              actionSaving={actionSaving}
            />
          </div>
        </div>
      )}

      {/* ── Create / Edit (reuses the shared RefundForm) ── */}
      {(modal === "create" || modal === "edit") && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          style={{ background: "rgba(10,15,30,0.66)", backdropFilter: "blur(10px)" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col"
            style={{ animation: "modalIn .2s ease" }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <h2 className="text-sm font-black text-slate-800">
                {modal === "edit" ? "Edit Refund" : "New Refund"} · {client.fullName}
              </h2>
              <button
                onClick={() => setModal(null)}
                className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 transition-colors"
              >
                <Ico d="M6 18L18 6M6 6l12 12" sz={12} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <RefundForm
                initial={modal === "edit" ? selected : refundInitial}
                clients={[client]}
                receipts={[]}
                saving={saving}
                onSubmit={modal === "edit" ? doEdit : doCreate}
                onClose={() => setModal(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Add a task linked to this refund ── */}
      {modal === "task" && selected && (
        <QuickTaskModal
          refundId={selected._id}
          clientId={selected.client?._id || selected.client || client._id}
          clientName={`${selected.refundReference} · ${client.fullName}`}
          onDone={() => {
            setModal(null);
            api.get(`/crm/tasks/by-refund/${selected._id}`).then(setRefundTasks).catch(() => {});
          }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}